import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, X, Loader2, Send, Inbox, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = {
  add_zips: "Add Zips",
  remove_zips: "Remove Zips",
  replace_zips: "Replace Zips",
};

const Approvals = () => {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // selected approval doc
  const [submitting, setSubmitting] = useState(false);
  const [denying, setDenying] = useState(false);
  const [denyMode, setDenyMode] = useState(false); // show the deny note panel
  const [denyNote, setDenyNote] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Form fields
  const [companyName, setCompanyName] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [replaceAllZips, setReplaceAllZips] = useState("");
  const [addZips, setAddZips] = useState("");
  const [zipNotToAdd, setZipNotToAdd] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/approvals`, { params: { status: statusFilter } });
      setApprovals(res.data || []);
    } catch (e) {
      console.error("Failed to load approvals:", e);
      toast.error("Failed to load approvals");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openReview = (req) => {
    setActive(req);
    setCompanyName(req.customer_name || "");
    setCompanyEmail(req.company_email || "");
    setReplaceAllZips(req.request_type === "replace_zips" ? (req.message || "") : "");
    setAddZips(req.request_type === "add_zips" ? (req.message || "") : "");
    setZipNotToAdd(req.request_type === "remove_zips" ? (req.message || "") : "");
    setSpecialRequests("");
    setDenyMode(false);
    setDenyNote("");
  };

  const closeReview = () => {
    if (submitting || denying || deleting) return;
    setActive(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyName.trim() || !companyEmail.trim()) {
      toast.error("Company Name and Company Email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/approvals/${active.id}/send`, {
        company_name: companyName.trim(),
        company_email: companyEmail.trim(),
        replace_all_zips: replaceAllZips,
        add_zips: addZips,
        zip_not_to_add: zipNotToAdd,
        special_requests: specialRequests,
      });
      const recipients = res.data?.recipients || [];
      toast.success(`Changes sent to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}.`);
      setActive(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send changes.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Permanently delete this request? No notification will be sent. This cannot be undone.")) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/approvals/${active.id}`);
      toast.success("Request deleted.");
      setActive(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete.");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeny = async () => {
    if (!denyNote.trim()) {
      toast.error("Please add a note explaining why this is being denied.");
      return;
    }
    if (!companyEmail.trim()) {
      toast.error("Company Email is required so we can notify the customer.");
      return;
    }
    setDenying(true);
    try {
      const res = await axios.post(`${API}/admin/approvals/${active.id}/deny`, {
        company_name: companyName.trim(),
        company_email: companyEmail.trim(),
        note: denyNote.trim(),
      });
      const recipients = res.data?.recipients || [];
      toast.success(`Denial sent to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}.`);
      setActive(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send denial.");
    } finally {
      setDenying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground font-['Chivo']">Approvals</h1>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 bg-[#0d2a5a] border-[#1a3a6a] text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="denied">Denied</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="font-['Chivo'] text-base flex items-center gap-2">
            <Inbox size={18} className="text-[#f4cc1e]" />
            {statusFilter === "pending" ? "Pending zip change requests" :
             statusFilter === "approved" ? "Approved" :
             statusFilter === "denied" ? "Denied" : "All zip change requests"}
            <Badge variant="secondary" className="ml-2">{approvals.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="px-6 py-10 text-center text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
          ) : approvals.length === 0 ? (
            <div className="px-6 py-10 text-center text-muted-foreground">No items.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0d2a5a] text-[#f4cc1e] uppercase text-xs">
                  <tr>
                    <th className="text-left px-4 py-2 font-bold">Customer</th>
                    <th className="text-left px-4 py-2 font-bold">Type</th>
                    <th className="text-left px-4 py-2 font-bold hidden md:table-cell">Submitted By</th>
                    <th className="text-left px-4 py-2 font-bold hidden lg:table-cell">Message</th>
                    <th className="text-left px-4 py-2 font-bold hidden sm:table-cell">Submitted</th>
                    <th className="text-right px-4 py-2 font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {approvals.map((r) => (
                    <tr key={r.id} className="border-b border-[#1a3a6a] hover:bg-[#0d2a5a]/30">
                      <td className="px-4 py-3 text-white font-medium">{r.customer_name}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">{TYPE_LABEL[r.request_type] || r.request_type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-white/70 text-xs font-mono hidden md:table-cell">{r.customer_email || r.submitted_by || "—"}</td>
                      <td className="px-4 py-3 text-white/70 text-xs hidden lg:table-cell max-w-md truncate">{r.message}</td>
                      <td className="px-4 py-3 text-white/60 text-xs hidden sm:table-cell">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" onClick={() => openReview(r)} className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 h-8">
                          Review
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={!!active} onOpenChange={(open) => { if (!open) closeReview(); }}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-white">Campaign Change Request</DialogTitle>
            <DialogDescription className="text-white/60">
              Review the customer&apos;s request and send the change notification to the contractor and the admin team.
            </DialogDescription>
          </DialogHeader>
          {active && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {active.message && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/60">Customer&apos;s Original Message</Label>
                  <div className="mt-1 text-xs bg-[#0a1628] border border-[#1a3a6a] rounded-md p-2 whitespace-pre-wrap text-white/80 font-mono max-h-32 overflow-y-auto">
                    {active.message}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="approval-company-name" className="text-white/80">Company Name *</Label>
                  <Input id="approval-company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required className="bg-[#0a1628] border-[#1a3a6a] text-white" />
                </div>
                <div>
                  <Label htmlFor="approval-company-email" className="text-white/80">Company Email *</Label>
                  <Input id="approval-company-email" value={companyEmail} onChange={(e) => setCompanyEmail(e.target.value)} required placeholder="leads@example.com, ops@example.com" className="bg-[#0a1628] border-[#1a3a6a] text-white text-xs font-mono" />
                </div>
              </div>
              <div>
                <Label htmlFor="approval-replace-all" className="text-white/80">Replace ALL ZIPS with</Label>
                <Textarea id="approval-replace-all" value={replaceAllZips} onChange={(e) => setReplaceAllZips(e.target.value)} placeholder="N/A" className="bg-[#0a1628] border-[#1a3a6a] text-white text-sm min-h-[60px] font-mono" />
              </div>
              <div>
                <Label htmlFor="approval-add" className="text-white/80">Add these ZIPS</Label>
                <Textarea id="approval-add" value={addZips} onChange={(e) => setAddZips(e.target.value)} placeholder="N/A" className="bg-[#0a1628] border-[#1a3a6a] text-white text-sm min-h-[60px] font-mono" />
              </div>
              <div>
                <Label htmlFor="approval-not-add" className="text-white/80">Zip NOT to add</Label>
                <Textarea id="approval-not-add" value={zipNotToAdd} onChange={(e) => setZipNotToAdd(e.target.value)} placeholder="N/A" className="bg-[#0a1628] border-[#1a3a6a] text-white text-sm min-h-[60px] font-mono" />
              </div>
              <div>
                <Label htmlFor="approval-special" className="text-white/80">Any Special Requests</Label>
                <Textarea id="approval-special" value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} className="bg-[#0a1628] border-[#1a3a6a] text-white text-sm min-h-[60px]" />
              </div>
              {denyMode && (
                <div className="bg-red-500/5 border border-red-500/30 rounded-md p-3 space-y-2">
                  <Label htmlFor="approval-deny-note" className="text-red-400 text-sm font-medium">Reason for denial (sent to the customer)</Label>
                  <Textarea
                    id="approval-deny-note"
                    value={denyNote}
                    onChange={(e) => setDenyNote(e.target.value)}
                    placeholder="Explain why this request can't be applied so the customer understands the next step."
                    autoFocus
                    className="bg-[#0a1628] border-[#1a3a6a] text-white text-sm min-h-[80px]"
                    maxLength={5000}
                  />
                </div>
              )}
              <DialogFooter className="gap-2 sm:justify-between">
                {!denyMode ? (
                  <Button type="button" variant="ghost" onClick={handleDelete} disabled={submitting || denying || deleting}
                    title="Permanently delete this request (no email sent)"
                    className="text-red-400/70 hover:text-red-400 hover:bg-red-500/10">
                    {deleting ? <Loader2 className="animate-spin mr-1" size={14} /> : <Trash2 size={14} className="mr-1" />}
                    Delete
                  </Button>
                ) : <span />}
                <div className="flex flex-wrap gap-2 justify-end">
                  {!denyMode && active.status !== "approved" && active.status !== "denied" && active.status !== "dismissed" && (
                    <Button type="button" variant="outline" onClick={() => setDenyMode(true)} disabled={submitting || deleting}
                      className="border-red-500/40 text-red-400 hover:bg-red-500/10">
                      <X size={14} className="mr-1" />
                      Deny
                    </Button>
                  )}
                  {denyMode && (
                    <>
                      <Button type="button" variant="ghost" onClick={() => { setDenyMode(false); setDenyNote(""); }} disabled={denying} className="text-white hover:bg-[#1a3a6a]">
                        Back
                      </Button>
                      <Button type="button" onClick={handleDeny} disabled={denying || !denyNote.trim()}
                        className="bg-red-600 text-white hover:bg-red-700">
                        {denying ? <Loader2 className="animate-spin mr-1" size={14} /> : <Send size={14} className="mr-1" />}
                        Confirm Deny &amp; Send
                      </Button>
                    </>
                  )}
                  {!denyMode && (
                    <>
                      <Button type="button" variant="ghost" onClick={closeReview} disabled={submitting || denying || deleting} className="text-white hover:bg-[#1a3a6a]">
                        Cancel
                      </Button>
                      <Button type="submit" disabled={submitting || denying || deleting || active.status === "approved"}
                        className="bg-green-600 text-white hover:bg-green-700">
                        {submitting ? <Loader2 className="animate-spin mr-1" size={14} /> : <CheckCircle2 size={14} className="mr-1" />}
                        Approve
                      </Button>
                    </>
                  )}
                </div>
              </DialogFooter>
              {active.status === "approved" && (
                <p className="text-xs text-green-400 flex items-center gap-1"><CheckCircle2 size={12} /> Already approved — re-sending will send another notification.</p>
              )}
              {(active.status === "denied" || active.status === "dismissed") && (
                <p className="text-xs text-red-400 flex items-center gap-1"><X size={12} /> Already denied.{active.denial_note ? ` Note: "${active.denial_note}"` : ""}</p>
              )}
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Approvals;
